import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useAppSelector } from "eduid-hooks";
import { FormattedMessage, defineMessages } from "react-intl";
const BADGE_CONFIG = defineMessages({
    complete: {
        id: "entry.complete.badge",
        defaultMessage: "COMPLETE",
        description: "complete badge",
    },
    recommended: {
        id: "entry.recommendation.badge",
        defaultMessage: "RECOMMENDED",
        description: "recommendation badge",
    },
    required: {
        id: "entry.requirement.badge",
        defaultMessage: "REQUIRED",
        description: "required badge",
    },
});
const Badge = ({ type }) => {
    const { id, defaultMessage, description } = BADGE_CONFIG[type];
    return (_jsx("div", { className: "recommendation-box", children: _jsx("span", { className: "badge", children: _jsx(FormattedMessage, { id: id, defaultMessage: defaultMessage, description: description }) }) }));
};
const MESSAGES = defineMessages({
    allComplete: {
        id: "entry.allComplete",
        defaultMessage: "You're all set to access {serviceName}.",
        description: "all requirements complete",
    },
    securityKeyRegistered: {
        id: "entry.securityKeyRegistered",
        defaultMessage: "You're ready to continue to {serviceName}.",
        description: "security key registered success",
    },
    verifiedCredentialsStep: {
        id: "entry.recommendation.al3.verified.credentialsStep",
        defaultMessage: "Your identity is verified to access {serviceName}. We recommend registering your sign-in method below for stronger account protection.",
        description: "al3 recommendation after verification, credentials step",
    },
    verifiedUserCreated: {
        id: "entry.recommendation.al3.verified.userCreated",
        defaultMessage: "Your identity is verified to access {serviceName}. We recommend registering a sign-in method on the Security page in eduID for stronger account protection.",
        description: "al3 recommendation after verification, user created",
    },
    verified: {
        id: "entry.recommendation.al3.verified",
        defaultMessage: "Your identity is verified to access {serviceName}. We recommend registering your sign-in method in Step 4 for stronger account protection.",
        description: "al3 recommendation after verification",
    },
    unverifiedPastEntry: {
        id: "entry.requirement.verifiedIdentity.pastEntry",
        defaultMessage: "A verified identity is required to access {serviceName}. Press Cancel to go back to Step 1, or verify your identity later on the Identity page in eduID.",
        description: "require verified identity, past entry",
    },
    unverifiedUserCreated: {
        id: "entry.requirement.verifiedIdentity.userCreated",
        defaultMessage: "A verified identity is required to access {serviceName}. Verify your identity on the Identity page in eduID.",
        description: "require verified identity, user created",
    },
    requirementStep4: {
        id: "entry.requirement.step4",
        defaultMessage: "Multi-factor authentication is required to access {serviceName}. We recommend registering with a digital ID, or register a security key in Step 4.",
        description: "require security key at step 4",
    },
    requirementCredentialsStep: {
        id: "entry.requirement.credentialsStep",
        defaultMessage: "Multi-factor authentication is required to access {serviceName}. Register a security key below, or press Cancel to go back and register with a digital ID (recommended).",
        description: "require security key on credentials step",
    },
    recommendationAl3: {
        id: "entry.recommendation.al3",
        defaultMessage: "A verified identity is required to access {serviceName}. Register with a digital ID below to complete this now.",
        description: "al3 recommendation",
    },
    requirementMfaEntry: {
        id: "entry.requirement.mfaEntry",
        defaultMessage: "Multi-factor authentication is required to access {serviceName}. We recommend registering with a digital ID below, or you can register a security key in Step 4.",
        description: "require mfa at entry, prefer digital ID",
    },
});
const getVerified = ({ isVerified, credentialsCompleted, nextPage }) => {
    if (!isVerified)
        return null;
    if (credentialsCompleted) {
        return { badgeType: "complete", success: true, message: MESSAGES.allComplete };
    }
    if (nextPage === "SIGNUP_CREDENTIALS") {
        return { badgeType: "recommended", message: MESSAGES.verifiedCredentialsStep };
    }
    if (nextPage === "SIGNUP_USER_CREATED") {
        return { badgeType: "recommended", message: MESSAGES.verifiedUserCreated };
    }
    return { badgeType: "recommended", message: MESSAGES.verified };
};
const getUnverified = ({ credentialsCompleted, requireMfa, needsDigitalId, pastPage, nextPage, }) => {
    // Security key registered and only MFA was required (no digital ID requirement) → done
    if (nextPage === "SIGNUP_CREDENTIALS" && credentialsCompleted && requireMfa && !needsDigitalId) {
        return { badgeType: "complete", success: true, message: MESSAGES.securityKeyRegistered };
    }
    // al2 or al3: a verified identity is required — security key alone will not satisfy it
    if (needsDigitalId) {
        if (nextPage === "SIGNUP_USER_CREATED") {
            return { badgeType: "required", message: MESSAGES.unverifiedUserCreated };
        }
        if (pastPage || nextPage === "SIGNUP_CREDENTIALS") {
            return { badgeType: "required", message: MESSAGES.unverifiedPastEntry };
        }
        return { badgeType: "required", message: MESSAGES.recommendationAl3 };
    }
    // Only require_mfa: a security key is sufficient, but digital ID is preferred
    if (requireMfa) {
        if (pastPage) {
            return { badgeType: "required", message: MESSAGES.requirementStep4 };
        }
        if (nextPage === "SIGNUP_CREDENTIALS") {
            return { badgeType: "required", message: MESSAGES.requirementCredentialsStep };
        }
        return { badgeType: "required", message: MESSAGES.requirementMfaEntry };
    }
    return null;
};
const getRecommendation = (input) => {
    return input.isVerified ? getVerified(input) : getUnverified(input);
};
export const ServiceInfo = () => {
    var _a, _b, _c, _d, _e, _f;
    const signup = useAppSelector((state) => state.signup);
    const authnReq = (_a = signup === null || signup === void 0 ? void 0 : signup.state) === null || _a === void 0 ? void 0 : _a.idp_authn_requirements;
    const externalMfa = (_b = signup === null || signup === void 0 ? void 0 : signup.state) === null || _b === void 0 ? void 0 : _b.external_mfa;
    const idp_service_info = (_c = signup === null || signup === void 0 ? void 0 : signup.state) === null || _c === void 0 ? void 0 : _c.idp_service_info;
    const credentialsCompleted = (_d = signup === null || signup === void 0 ? void 0 : signup.state) === null || _d === void 0 ? void 0 : _d.credentials.webauthn_registered;
    const locale = useAppSelector((state) => state.intl.locale);
    const service_name = ((_e = idp_service_info === null || idp_service_info === void 0 ? void 0 : idp_service_info.display_name) === null || _e === void 0 ? void 0 : _e[locale]) || ((_f = idp_service_info === null || idp_service_info === void 0 ? void 0 : idp_service_info.display_name) === null || _f === void 0 ? void 0 : _f["en"]) || undefined;
    if (!service_name)
        return null;
    const requireMfa = authnReq === null || authnReq === void 0 ? void 0 : authnReq.require_mfa;
    const level = authnReq === null || authnReq === void 0 ? void 0 : authnReq.minimum_assurance_level;
    const hasRequirements = requireMfa || level;
    const isVerified = Boolean(externalMfa);
    const nextPage = signup.next_page;
    const renderRecommendation = () => {
        const pastPage = nextPage === "SIGNUP_CAPTCHA" || nextPage === "SIGNUP_TOU" || nextPage === "SIGNUP_ENTER_CODE";
        const serviceNameValue = { serviceName: _jsx("strong", { children: service_name }) };
        const needsDigitalId = level === "al3" || level === "al2";
        const recommendation = getRecommendation({
            isVerified,
            credentialsCompleted: Boolean(credentialsCompleted),
            requireMfa: Boolean(requireMfa),
            needsDigitalId,
            pastPage,
            nextPage,
        });
        if (!recommendation)
            return null;
        return (_jsxs(_Fragment, { children: [_jsx(Badge, { type: recommendation.badgeType }), _jsx("span", { className: "suggestion-txt md", children: _jsx(FormattedMessage, { id: recommendation.message.id, defaultMessage: recommendation.message.defaultMessage, description: recommendation.message.description, values: serviceNameValue }) })] }));
    };
    return (_jsx(_Fragment, { children: hasRequirements ? (_jsx("div", { className: "access-requirements", children: renderRecommendation() })) : (_jsx("div", { className: "destination-info", children: _jsx("p", { className: "text-bold", children: _jsx(FormattedMessage, { id: "entry.accessIntro", defaultMessage: "In order to access {name}", description: "Signup first page lead text", values: { name: _jsx("span", { children: service_name }) } }) }) })) }));
};
